import React from 'react';
import { PolyominoGrid, polyominoSpan } from './figures';

// The easiest rung of the served administration. response_window_ms decays from
// 9000ms to 1500ms as the ladder climbs, so this constant is only ever reached
// when the block carries no params at all.
const DEFAULT_WINDOW_MS = 9000;

// This instrument serves no inter-stimulus interval. The fixation and the gap
// are fixed administration constants that sit ON TOP of the served response
// window. Flanker and stroop fit their fixation INSIDE a served isi_ms; that
// convention belongs to those instruments and is not imported here.
const FIXATION_MS = 300;
const GAP_MS = 350;

// ONE square is the same number of CSS pixels everywhere on screen: on every
// piece, on every option, and between the two. This is a judgement about area —
// two pieces of four squares make an eight-square shape — and it is not
// available at all when a two-square piece is drawn as large as a six-square
// one. So the CELL is the constant and the BOX follows the shape: a figure is
// drawn at cell x span and centred in its slot rather than stretched to fill it.
//
// The cell is derived once per trial from the served grid, and never per shape.
// A shape can overhang the assembly grid by a row or a column, so the budget is
// gridSize + 1 squares wide. At the served gridSize of 4 that is 90 / 5 = 18px
// per square, so the largest figure a trial can produce is 90px — inside the
// 96px minimum width of a response button, which is the narrowest slot any of
// these figures is drawn into and does not shrink below 96px on a 320px phone.
// A larger served grid shrinks every figure on screen together.
const FIGURE_BOX_PX = 90;
const DEFAULT_GRID = 4;
const GRID_OVERHANG = 1;

const cellPxFor = (stimulus) =>
  FIGURE_BOX_PX / ((Number(stimulus?.gridSize) || DEFAULT_GRID) + GRID_OVERHANG);

// Pieces are drawn at the orientation served and are never rotated or
// reflected. That is a rule of the task, not a simplification: with rotation
// allowed, "can these pieces make that shape" is a different and much larger
// question and a distractor could not be proved wrong. So there is deliberately
// no rotate control, no auto-orienting, and no aspect-ratio normalisation that
// would imply a piece can be turned.
//
// The pieces carry index-only accessible names. A name that described a piece's
// role — "the corner piece", "fits top left" — would hand a screen-reader user
// the fit that everyone else has to work out.
function StimulusView({ stimulus }) {
  const pieces = Array.isArray(stimulus?.pieces) ? stimulus.pieces : [];
  // The served grid and nothing else. Sizing the pieces off the options would
  // read the answer panel into the question panel.
  const cell = cellPxFor(stimulus);
  return (
    <div
      role="group"
      aria-label="Pieces"
      // Wraps rather than overflows: three pieces at the widest span a trial can
      // serve are wider than a phone, and a piece pushed off screen is a piece
      // the participant cannot use.
      style={{
        display: 'flex',
        gap: 'var(--space-4)',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
      }}
    >
      {pieces.map((piece, i) => (
        <PolyominoGrid
          key={i}
          cells={piece}
          size={cell * polyominoSpan(piece)}
          label={`Piece ${i + 1}`}
        />
      ))}
    </div>
  );
}

export const spatialAssemblyTask = {
  id: 'spatial-assembly',
  name: 'Fit the Pieces',
  pillar: 'spatial',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'You are shown two or three pieces.',
    'Pick the shape they make when slid together.',
    'Pieces may be slid but never turned or flipped.',
    'Answer before the timer runs out — a late answer counts as wrong.',
  ],

  // Options come from the stimulus, so there is no static target set. The
  // fallback is empty rather than a placeholder: a wrong-shaped target array
  // would be submitted as a real choice.
  targets: [],

  // Deliberately empty. A worked example has to name a correct response, and
  // which option is assemblable is exactly what the payload withholds — writing
  // one by hand would publish an answer key for a shape the server owns.
  examples: [],

  // The index is into `options` AS SERVED. The correct slot is uniformly
  // distributed across the four positions by design, so any client-side sort,
  // dedupe or reorder makes every graded index meaningless.
  toAnswer: (targetId) => ({ choice: Number(targetId) }),

  // Every option is a connected 8-cell shape, so nothing here may drop, merge
  // or re-order one: an option cannot be eliminated by counting cells or by
  // spotting a broken shape, and the served position is the graded answer.
  targetsFor: (trial) => {
    const options = trial?.stimulus?.options;
    if (!Array.isArray(options)) return [];
    // The served grid and nothing else. An option's drawing must follow its own
    // cells and a trial-wide constant, never the pieces it is judged against.
    const cell = cellPxFor(trial.stimulus);
    return options.map((cells, i) => ({
      id: String(i),
      label: `Option ${i + 1}`,
      keys: [String(i + 1)],
      hint: String(i + 1),
      node: (
        <PolyominoGrid
          cells={cells}
          size={cell * polyominoSpan(cells)}
          label={`Option ${i + 1}`}
        />
      ),
    }));
  },

  // The server marks a correct choice made after response_window_ms INCORRECT —
  // without that the ladder cannot bite, because an unhurried participant sits
  // at 1.00 accuracy and climbs past every window the ladder sets. So the client
  // window must equal the served one. piece_count is the other difficulty knob
  // and is already visible as the pieces themselves.
  //
  // Number() is load-bearing: the runner bounds reaction time at windowMs + 250,
  // so a window arriving as the string "9000" would concatenate to "9000250" and
  // accept every late response. `||` rather than `??` so a served 0 falls back
  // instead of timing every trial out instantly.
  timing: (params) => ({
    fixationMs: FIXATION_MS,
    windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
    gapMs: GAP_MS,
  }),

  // Which option is assemblable is not in the payload, and running the
  // translation-only tiling search on the client would be reconstructing a
  // withheld answer — so there is nothing to feed back, on a practice trial or
  // anywhere else.
  feedbackAvailable: false,
  practiceExpectation: () => null,

  StimulusView,
};
